/**
 * Web3Auth client singleton service for Solana.
 *
 * Initializes and provides access to Web3Auth for wallet-based authentication.
 */

import { Web3Auth, CHAIN_NAMESPACES, WEB3AUTH_NETWORK } from '@web3auth/modal';
import type { CustomChainConfig, IProvider, Web3AuthOptions } from '@web3auth/modal';
import { Connection, PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { signInWithCustomToken } from 'firebase/auth';
import { firebaseClient } from '../firebase';
import { buildCloudApiUrl } from '../urlResolver';
import { JSON_HEADERS } from '../../utils/httpUtils';
import { WEB3AUTH_ERRORS } from '../../constants/errorMessages';

/**
 * Web3Auth configuration from environment variables.
 */
const WEB3AUTH_CLIENT_ID = import.meta.env.VITE_WEB3AUTH_CLIENT_ID;

/**
 * Solana chain configuration for mainnet.
 *
 * Web3Auth v10 chain-id convention: Solana Mainnet = 0x65 (101), Testnet = 0x66,
 * Devnet = 0x67 (see SOLANA_CAIP_CHAIN_MAP in @web3auth/no-modal). The v9-era
 * value 0x1 is NOT recognized by the v10 Wallet Services embed.
 */
const SOLANA_CHAIN_CONFIG: CustomChainConfig = {
  chainNamespace: CHAIN_NAMESPACES.SOLANA,
  chainId: '0x65', // Solana Mainnet
  rpcTarget: 'https://api.mainnet-beta.solana.com',
  displayName: 'Solana Mainnet',
  blockExplorerUrl: 'https://explorer.solana.com',
  ticker: 'SOL',
  tickerName: 'Solana',
  logo: 'https://images.toruswallet.io/solana.svg',
  decimals: 9,
};

/**
 * An Ed25519 signature is 64 bytes. The API refuses anything else, and so does
 * this: a signature of the wrong length means the provider handed back something
 * that is not a raw signature, and guessing at its shape here would put garbage
 * on the wire and turn a clear failure into an opaque 401.
 */
const ED25519_SIGNATURE_BYTES = 64;

/**
 * What the wallet returned was not a raw Ed25519 signature.
 *
 * A local constant rather than an entry in `constants/errorMessages`: this
 * string never reaches a user. It is a developer-facing assertion about a
 * provider contract, thrown into the console path below.
 */
const SIGNATURE_SHAPE_ERROR =
  '[Web3Auth] The Solana provider did not return a 64-byte Ed25519 signature; cannot prove wallet control.';

/**
 * Encode raw signature bytes as standard base64, the way the API expects them.
 *
 * Base64 rather than the base58 the Solana ecosystem often uses: both ends
 * already have a strict, padded decoder for base64, and base58 would mean taking
 * a direct dependency on an encoder this bundle currently only has transitively.
 *
 * @param signed - Whatever the provider returned from `signMessage`
 * @returns Base64 of exactly 64 signature bytes
 * @throws If the value is not a 64-byte byte array — deliberately loud, never a
 *   best-effort conversion.
 */
function toBase64Signature(signed: unknown): string {
  const bytes =
    signed instanceof Uint8Array
      ? signed
      : Array.isArray(signed)
        ? Uint8Array.from(signed as number[])
        : null;

  if (!bytes || bytes.length !== ED25519_SIGNATURE_BYTES) {
    throw new Error(SIGNATURE_SHAPE_ERROR);
  }

  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

/**
 * The challenge `POST /api/v1/auth/nonce` returns.
 */
interface IWalletLoginChallenge {
  /** Single-use nonce, presented back at /custom-token. */
  nonce: string;
  /** The exact string to sign. Signed verbatim — never rebuilt on this side. */
  message: string;
}

/**
 * Wallet user information from Web3Auth.
 */
export interface IWeb3AuthUser {
  /** Solana wallet public key (address) */
  walletAddress: string;
  /** User email (if provided during social login) */
  email?: string;
  /** User name (if provided) */
  name?: string;
  /** Profile image URL */
  profileImage?: string;
  /** Authentication method used */
  authMethod: 'wallet' | 'social';
  /** SOL balance in lamports */
  balance?: number;
}

/**
 * Web3Auth client singleton class.
 *
 * Provides centralized access to Web3Auth services with lazy initialization.
 */
class Web3AuthClient {
  private static instance: Web3AuthClient;
  private web3auth: Web3Auth | null = null;
  private provider: IProvider | null = null;
  private solanaConnection: Connection | null = null;
  private initialized = false;

  /**
   * Private constructor to prevent direct instantiation.
   */
  private constructor() {}

  /**
   * Get singleton instance of Web3AuthClient.
   *
   * @returns Web3AuthClient singleton instance
   */
  public static getInstance(): Web3AuthClient {
    if (!Web3AuthClient.instance) {
      Web3AuthClient.instance = new Web3AuthClient();
    }
    return Web3AuthClient.instance;
  }

  /**
   * Initialize Web3Auth instance.
   * Must be called before using any other methods.
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!WEB3AUTH_CLIENT_ID) {
      throw new Error(WEB3AUTH_ERRORS.CLIENT_ID_NOT_CONFIGURED);
    }

    try {
      // Web3Auth v10-native config: chains are declared here (merged with the
      // dashboard project config; local entries take precedence). Social logins
      // get their Solana provider from the built-in Wallet Services embed --
      // the v9 SolanaPrivateKeyProvider/privateKeyProvider path was removed
      // together with the dead @web3auth/base@9 / @web3auth/solana-provider@9
      // packages.
      const options: Web3AuthOptions = {
        clientId: WEB3AUTH_CLIENT_ID,
        web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_MAINNET,
        chains: [SOLANA_CHAIN_CONFIG],
        defaultChainId: SOLANA_CHAIN_CONFIG.chainId,
        uiConfig: {
          appName: 'Asgard Studios',
          mode: 'dark',
          loginMethodsOrder: ['google', 'facebook', 'twitter', 'discord'],
          logoLight: 'https://asgardstudio.net/logo.png',
          logoDark: 'https://asgardstudio.net/logo.png',
          defaultLanguage: 'en',
        },
      };
      this.web3auth = new Web3Auth(options);

      await this.web3auth.init();

      // Initialize Solana connection
      this.solanaConnection = new Connection(SOLANA_CHAIN_CONFIG.rpcTarget, 'confirmed');

      this.initialized = true;
      console.log('[Web3Auth] Initialized successfully');
    } catch (error) {
      console.error('[Web3Auth] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Check if Web3Auth is initialized.
   *
   * @returns True if initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Connect wallet using Web3Auth modal.
   * Opens modal with all available login options.
   * Also signs in to Firebase with a custom token for feature access.
   *
   * @returns Promise with wallet user info
   */
  public async connect(): Promise<IWeb3AuthUser> {
    if (!this.web3auth) {
      throw new Error(WEB3AUTH_ERRORS.NOT_INITIALIZED);
    }

    try {
      this.provider = await this.web3auth.connect();

      if (!this.provider) {
        throw new Error(WEB3AUTH_ERRORS.CONNECTION_FAILED);
      }

      const userInfo = await this.getUserInfo();

      // Sign in to Firebase with custom token for feature access (Voice Cloning, etc.)
      await this.signInToFirebase(userInfo);

      return userInfo;
    } catch (error) {
      console.error('[Web3Auth] Connection failed:', error);
      throw error;
    }
  }

  /**
   * Ensure the user is signed in to Firebase.
   * Call this for existing sessions to enable Firebase-protected features.
   *
   * @returns Promise that resolves when Firebase sign-in is complete
   */
  public async ensureFirebaseAuth(): Promise<void> {
    if (!this.isConnected()) {
      throw new Error(WEB3AUTH_ERRORS.NOT_CONNECTED);
    }

    // Check if already signed in to Firebase
    const auth = firebaseClient.getAuth();
    if (auth?.currentUser) {
      console.log('[Web3Auth] Already signed in to Firebase');
      return;
    }

    const userInfo = await this.getUserInfo();
    await this.signInToFirebase(userInfo);
  }

  /**
   * Sign in to Firebase using a custom token.
   * This enables Web3Auth users to access Firebase-protected features.
   *
   * The wallet address IS the Firebase UID, so the server will not mint on an
   * address alone — it used to, which made every wallet account takeable over by
   * anyone who could read the address off a block explorer. Two round trips now:
   * ask for a challenge, sign exactly the message that came back, present both.
   *
   * @param userInfo - Web3Auth user info
   */
  private async signInToFirebase(userInfo: IWeb3AuthUser): Promise<void> {
    try {
      // /api/v1/auth/* is Cloud-Run-only — use buildCloudApiUrl so wallet login
      // would resolve to Cloud Run on desktop too (currently gated by
      // WALLET_LOGIN_ENABLED=false, fixed here to avoid a latent localhost->404).
      const nonceResponse = await fetch(buildCloudApiUrl('/api/v1/auth/nonce'), {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ wallet_address: userInfo.walletAddress }),
      });

      if (!nonceResponse.ok) {
        const error = await nonceResponse.json().catch(() => ({}));
        console.error('[Web3Auth] Failed to get a login challenge:', error);
        return; // Don't fail the whole login if Firebase auth fails
      }

      const { nonce, message } = (await nonceResponse.json()) as IWalletLoginChallenge;

      // Sign the server's message VERBATIM. Rebuilding it here would be a second
      // copy of the format, free to drift from the one the server verifies
      // against — and the server rebuilds it too, so a mismatch is a hard 401
      // rather than a warning.
      const signature = toBase64Signature(await this.signMessage(message));

      const response = await fetch(buildCloudApiUrl('/api/v1/auth/custom-token'), {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          wallet_address: userInfo.walletAddress,
          nonce,
          signature,
          email: userInfo.email,
          name: userInfo.name,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('[Web3Auth] Failed to get custom token:', error);
        return; // Don't fail the whole login if Firebase auth fails
      }

      const { custom_token } = await response.json();

      // Sign in to Firebase with the custom token
      const auth = firebaseClient.getAuth();
      if (!auth) {
        console.warn('[Web3Auth] Firebase auth unavailable; skipping Firebase sign-in');
        return;
      }
      await signInWithCustomToken(auth, custom_token);

      console.log('[Web3Auth] Firebase sign-in successful');
    } catch (error) {
      console.error('[Web3Auth] Firebase sign-in failed:', error);
      // Don't throw - Web3Auth login still succeeded
    }
  }

  /**
   * Disconnect wallet and clear session.
   */
  public async disconnect(): Promise<void> {
    if (!this.web3auth) {
      return;
    }

    try {
      await this.web3auth.logout();
      this.provider = null;
      console.log('[Web3Auth] Disconnected');
    } catch (error) {
      console.error('[Web3Auth] Disconnect failed:', error);
      throw error;
    }
  }

  /**
   * Check if user is currently connected.
   *
   * @returns True if connected
   */
  public isConnected(): boolean {
    return this.web3auth?.connected ?? false;
  }

  /**
   * Get current user information.
   *
   * @returns Promise with wallet user info
   */
  public async getUserInfo(): Promise<IWeb3AuthUser> {
    if (!this.web3auth || !this.provider) {
      throw new Error(WEB3AUTH_ERRORS.NOT_CONNECTED);
    }

    try {
      // Get user info from Web3Auth
      const userInfo = await this.web3auth.getUserInfo();

      // Get wallet address from provider
      const accounts = (await this.provider.request({
        method: 'getAccounts',
      })) as string[] | undefined;

      const walletAddress = accounts?.[0] ?? '';

      if (!walletAddress) {
        throw new Error(WEB3AUTH_ERRORS.WALLET_ADDRESS_FAILED);
      }

      // Get SOL balance
      let balance: number | undefined;
      if (this.solanaConnection && walletAddress) {
        try {
          const publicKey = new PublicKey(walletAddress);
          const lamports = await this.solanaConnection.getBalance(publicKey);
          balance = lamports / LAMPORTS_PER_SOL;
        } catch (balanceError) {
          console.warn('[Web3Auth] Failed to get balance:', balanceError);
        }
      }

      return {
        walletAddress,
        email: userInfo.email,
        name: userInfo.name,
        profileImage: userInfo.profileImage,
        authMethod: userInfo.email ? 'social' : 'wallet',
        balance,
      };
    } catch (error) {
      console.error('[Web3Auth] Failed to get user info:', error);
      throw error;
    }
  }

  /**
   * Get Solana provider for transactions.
   *
   * @returns IProvider instance or null
   */
  public getProvider(): IProvider | null {
    return this.provider;
  }

  /**
   * Get Solana connection for RPC calls.
   *
   * @returns Connection instance or null
   */
  public getSolanaConnection(): Connection | null {
    return this.solanaConnection;
  }

  /**
   * Sign a message with the connected wallet.
   *
   * @param message - Message to sign
   * @returns Signed message as Uint8Array
   */
  public async signMessage(message: string): Promise<Uint8Array> {
    if (!this.provider) {
      throw new Error(WEB3AUTH_ERRORS.NOT_CONNECTED);
    }

    const encodedMessage = new TextEncoder().encode(message);
    const signedMessage = await this.provider.request({
      method: 'signMessage',
      params: {
        message: encodedMessage,
      },
    });

    return signedMessage as Uint8Array;
  }
}

// Export singleton instance
export const web3AuthClient = Web3AuthClient.getInstance();
